import { keepPreviousData } from '@tanstack/react-query';
import { Edit, Plus, Save, Settings, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Textarea,
} from '@virtual-care/shadcn';
import { SearchInput, useAction, useData } from '@virtual-care/ui';

import { CountablePageHeader } from '../../components/page-header.tsx';

interface Setting {
  key: string;
  value: string;
  description?: string | null;
  section: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CreateSettingData {
  key: string;
  value: string;
  description?: string;
  section: string;
  isActive: boolean;
}

interface UpdateSettingData {
  value?: string;
  description?: string;
  section?: string;
  isActive?: boolean;
}

export default function SettingsAdminPage() {
  const [searchQuery, setSearchQuery] = useState<string | undefined>('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState<{
    key: string;
    section: string;
  } | null>(null);
  const [editData, setEditData] = useState<UpdateSettingData>({});
  const [createData, setCreateData] = useState<CreateSettingData>({
    key: '',
    value: '',
    description: '',
    section: 'general',
    isActive: true,
  });

  // Fetch settings
  const { data: settingsResponse, isLoading } = useData(
    'GET /settings',
    { key: searchQuery || undefined },
    { placeholderData: keepPreviousData },
  );

  const createMutation = useAction('POST /settings', {
    invalidate: ['GET /settings'],
    onSuccess: () => {
      toast.success('Setting created successfully');
      setIsCreateDialogOpen(false);
      setCreateData({
        key: '',
        value: '',
        description: '',
        section: 'general',
        isActive: true,
      });
    },
    onError: (error) => {
      toast.error(
        `Failed to create setting: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    },
  });

  const updateMutation = useAction('PUT /settings/{oldSection}/{oldKey}', {
    invalidate: ['GET /settings'],
    onSuccess: () => {
      toast.success('Setting updated successfully');
      setEditingSetting(null);
      setEditData({});
    },
    onError: (error) => {
      toast.error(
        `Failed to update setting: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    },
  });

  const deleteMutation = useAction('DELETE /settings/{section}/{key}', {
    invalidate: ['GET /settings'],
    onSuccess: () => {
      toast.success('Setting deleted successfully');
    },
    onError: (error) => {
      toast.error(
        `Failed to delete setting: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    },
  });

  const settings = settingsResponse?.data || [];

  const handleEdit = (setting: Setting) => {
    setEditingSetting({ key: setting.key, section: setting.section });
    setEditData({
      value: setting.value,
      description: setting.description || '',
      section: setting.section,
      isActive: setting.isActive,
    });
  };

  const handleSaveEdit = () => {
    if (!editingSetting) return;
    updateMutation.mutate({
      oldKey: editingSetting.key,
      oldSection: editingSetting.section,
      ...editData,
    });
  };

  const handleCancelEdit = () => {
    setEditingSetting(null);
    setEditData({});
  };

  const handleDelete = (setting: Setting) => {
    if (window.confirm('Are you sure you want to delete this setting?')) {
      deleteMutation.mutate({ section: setting.section, key: setting.key });
    }
  };

  const handleCreate = () => {
    if (!createData.key.trim() || !createData.value.trim()) {
      toast.error('Key and value are required');
      return;
    }
    createMutation.mutate(createData);
  };

  return (
    <div className="space-y-6">
      <CountablePageHeader
        title="Settings"
        totalCount={`${settings.length} total settings`}
        icon={Settings}
      />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Setting
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[525px]">
            <DialogHeader>
              <DialogTitle>Create New Setting</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="key">Key</Label>
                <Input
                  id="key"
                  value={createData.key}
                  onChange={(e) =>
                    setCreateData({ ...createData, key: e.target.value })
                  }
                  placeholder="e.g., supportPhoneNumber"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="value">Value</Label>
                <Input
                  id="value"
                  value={createData.value}
                  onChange={(e) =>
                    setCreateData({ ...createData, value: e.target.value })
                  }
                  placeholder="Setting value"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="section">Section</Label>
                <Input
                  id="section"
                  value={createData.section}
                  onChange={(e) =>
                    setCreateData({ ...createData, section: e.target.value })
                  }
                  placeholder="e.g., general, notifications, security"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={createData.description}
                  onChange={(e) =>
                    setCreateData({
                      ...createData,
                      description: e.target.value,
                    })
                  }
                  placeholder="Optional description"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isActive"
                  checked={createData.isActive}
                  onCheckedChange={(checked: boolean) =>
                    setCreateData({ ...createData, isActive: checked })
                  }
                />
                <Label htmlFor="isActive">Active</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating...' : 'Create'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center space-x-2">
        <SearchInput
          placeholder="Search settings..."
          value={searchQuery}
          onChange={setSearchQuery}
        />
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="space-y-2">
                <div className="bg-muted h-4 w-3/4 rounded"></div>
                <div className="bg-muted h-3 w-1/2 rounded"></div>
              </CardHeader>
              <CardContent>
                <div className="bg-muted h-4 w-full rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {settings.map((setting: Setting) => (
            <Card key={setting.key} className="relative">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-1">
                    <CardTitle className="text-sm font-medium">
                      {editingSetting?.key === setting.key &&
                      editingSetting?.section === setting.section ? (
                        <Input
                          value={setting.key}
                          disabled
                          className="text-sm font-medium"
                        />
                      ) : (
                        setting.key
                      )}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {editingSetting?.key === setting.key &&
                        editingSetting?.section === setting.section ? (
                          <Input
                            value={editData.section ?? setting.section}
                            onChange={(e) =>
                              setEditData({
                                ...editData,
                                section: e.target.value,
                              })
                            }
                            placeholder="Section"
                            className="h-6 text-xs"
                          />
                        ) : (
                          setting.section
                        )}
                      </Badge>
                    </div>
                  </div>
                  <Badge variant={setting.isActive ? 'default' : 'secondary'}>
                    {editingSetting?.key === setting.key &&
                    editingSetting?.section === setting.section ? (
                      <Checkbox
                        checked={editData.isActive ?? setting.isActive}
                        onCheckedChange={(checked: boolean) =>
                          setEditData({ ...editData, isActive: checked })
                        }
                      />
                    ) : setting.isActive ? (
                      'Active'
                    ) : (
                      'Inactive'
                    )}
                  </Badge>
                </div>
                {(setting.description ||
                  (editingSetting?.key === setting.key &&
                    editingSetting?.section === setting.section)) && (
                  <CardDescription>
                    {editingSetting?.key === setting.key &&
                    editingSetting?.section === setting.section ? (
                      <Textarea
                        value={
                          editData.description ?? setting.description ?? ''
                        }
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            description: e.target.value,
                          })
                        }
                        placeholder="Optional description"
                        className="text-xs"
                      />
                    ) : (
                      setting.description
                    )}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <Label className="text-muted-foreground text-xs">
                      Value
                    </Label>
                    {editingSetting?.key === setting.key &&
                    editingSetting?.section === setting.section ? (
                      <Input
                        value={editData.value ?? setting.value}
                        onChange={(e) =>
                          setEditData({ ...editData, value: e.target.value })
                        }
                        className="mt-1"
                      />
                    ) : (
                      <p className="mt-1 font-mono text-sm break-all">
                        {setting.value}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-between border-t pt-2">
                    {editingSetting?.key === setting.key &&
                    editingSetting?.section === setting.section ? (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleSaveEdit}
                          disabled={updateMutation.isPending}
                        >
                          <Save className="mr-1 h-3 w-3" />
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancelEdit}
                        >
                          <X className="mr-1 h-3 w-3" />
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(setting)}
                        >
                          <Edit className="mr-1 h-3 w-3" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(setting)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="mr-1 h-3 w-3" />
                          Delete
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!isLoading && settings.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Settings className="text-muted-foreground mb-4 h-12 w-12" />
            <h3 className="mb-2 text-lg font-semibold">No settings found</h3>
            <p className="text-muted-foreground mb-4 text-center">
              {searchQuery
                ? `No settings match "${searchQuery}"`
                : 'Get started by creating your first setting'}
            </p>
            {!searchQuery && (
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Setting
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
